import React from 'react'
import { useState, useEffect, useRef } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import cogoToast from '@successtar/cogo-toast'
import { useConfigStore } from '@renderer/stores/configState'
import { FrameOption, FrameConfig } from '@renderer/types/configState'
import PrintPreview, { PrintPreviewRef } from '@renderer/components/PrintPreview'
import { ProcessingModal } from '@renderer/components'
import PhotoFrame from '@renderer/components/PhotoFrame'
import { FaExchangeAlt } from 'react-icons/fa'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { backgroundVideoProcessor } from '@renderer/utils/backgroundVideoProcessor'
import { usePrintStore } from '@renderer/stores/printStore'
import { useTranslation } from 'react-i18next'
import { useSharedCountdown } from './hooks/useSharedCountdown'
import {
  processAndUploadStrip,
  processAndUploadGIF,
  processAndUploadPhotos,
  stateUploader
} from '@renderer/utils/pipeline/outputOrchestrator'
import { initiatePrint } from '@renderer/utils/pipeline/printService'
import type { ProcessingContext } from '@renderer/types/outputPipeline.types'
import { waitPrintResultMinDisplay } from '@renderer/utils/printResultMinDisplay'
import { navigateAfterOutputsReady } from '@renderer/utils/navigateAfterOutputs'

interface UserContext {
  user?: {
    email: string
  }
}

const SelectPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const context = useOutletContext<UserContext | null>()
  const {
    gallery,
    galleryKey,
    setSessionState,
    userPreferences,
    id,
    logActivity
  } = useSessionStateStore()
  const { getFrame, config: globalConfig, frames } = useConfigStore()
  const config = globalConfig?.pages.select
  const captureConfig = globalConfig?.pages.capture
  const captureMode = captureConfig?.settings.mode || 'standard'
  const { primaryColor } = globalConfig?.theme || {
    primaryColor: '#3B82F6',
    secondaryColor: '#FFFFFF'
  }
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const previewRef = useRef<PrintPreviewRef>(null)
  const [mirroredPhotos, setMirroredPhotos] = useState<Set<string>>(new Set())
  const [isAllMirrored, setIsAllMirrored] = useState(false)
  const [isSelectionLocked, setIsSelectionLocked] = useState(false)
  const { downloadLocal } = useSettingsStore()
  const [videoProcessingId, setVideoProcessingId] = useState<string | null>(null)
  const { isPrintingEnabled, getPrinterForFrame } = usePrintStore()
  const uploadStartedRef = useRef<boolean>(false)
  const printJobSentRef = useRef<boolean>(false)
  const isOnlineRef = useRef<boolean>(true)
  const isMountedRef = useRef(true)
  const isProcessingRef = useRef(false)
  const navigationLockRef = useRef(false)
  const videoProcessingIdRef = useRef<string | null>(null)

  // Simple loading state management
  const [isPreviewReady, setIsPreviewReady] = useState(false)

  // Use shared countdown for consistent UI (uses captureTimeout from capture settings)
  const {
    remainingTime: sharedRemainingTime,
    isExpired: isSharedCountdownExpired
  } = useSharedCountdown()

  const getFrameConfig = (frame: FrameOption | null): FrameConfig => {
    if (!frame)
      return {
        id: '',
        baseImage: '',
        drawParams: {
          canvasWidth: 1200,
          canvasHeight: 1800,
          backgroundColor: '#FFFFFF',
          images: []
        }
      }
    return {
      id: frame._id,
      baseImage: frame.thumbnail,
      drawParams: frame.drawParams
    }
  }

  const { settings: currentPrinterSettings } = getPrinterForFrame(userPreferences.frameId || '')
  const printResolutionScale = currentPrinterSettings.printResolutionScale || 1

  // Frame-based mode setup
  const selectedFrame = captureMode === 'frame-based' ? getFrame(userPreferences.frameId || '') : null

  // IMPORTANT: Video processing must be isolated from print resolution scale
  // Print resolution scale can be 0.5x, 1x, 2x, 3x, etc., which would create
  // videos that are too large for codec limits (e.g., 2480x3508 * 3x = 7440x10524).
  // Video generation uses automatic resizing to cap at 1920px per dimension,
  // but we prevent unnecessary processing by using unscaled frames.

  // Get scaled frame for printing/preview (uses printResolutionScale)
  const maybeScaledFrame = getFrame(userPreferences.frameId || frames[0]._id, printResolutionScale)
  const selectedScaledFrame = maybeScaledFrame || null
  const scaledFrameConfig = getFrameConfig(selectedScaledFrame)

  // Get unscaled frame for video processing (ALWAYS use scale = 1)
  // This ensures video processing is independent of print resolution settings
  const maybeUnscaledFrame = getFrame(userPreferences.frameId || frames[0]._id, 1)
  const selectedUnscaledFrame = maybeUnscaledFrame || null
  const unscaledFrameConfig = getFrameConfig(selectedUnscaledFrame)

  // Use scaled frame config for print operations and preview display
  const frameConfig = scaledFrameConfig

  // Get unique photo types from frame configuration
  const uniquePhotoTypes = frameConfig.drawParams.images
    .filter((img) => img.type.startsWith('photo-'))
    .map((img) => img.type)
    .filter((value, index, self) => self.indexOf(value) === index)

  const maxSelectablePhotos = uniquePhotoTypes?.length || 0

  const isPotrait =
    (globalConfig?.screenSize.height || 1080) > (globalConfig?.screenSize.width || 1920)

  // Calculate scale for fullscreen frame display (same as Capture.tsx)
  const screenWidth = globalConfig?.screenSize.width || 1080
  const screenHeight = globalConfig?.screenSize.height || 1920
  const frameWidth = frameConfig.drawParams.canvasWidth
  const frameHeight = frameConfig.drawParams.canvasHeight
  
  // Scale frame to fit screen while maintaining aspect ratio
  // Account for padding, top gap (larger for header), and bottom whitespace for buttons
  const paddingSize = 16
  const topGap = 280 // Increased to prevent overlap with larger header
  const buttonAreaHeight = 180
  const availableWidth = screenWidth - paddingSize * 2
  const availableHeight = screenHeight - paddingSize * 2 - topGap - buttonAreaHeight
  // Apply additional scale reduction to make strip smaller
  const baseScale = Math.min(availableWidth / frameWidth, availableHeight / frameHeight)
  const scale = baseScale * 0.85 // Reduce scale by 15% to make strip smaller

  // FIX 1: Make handleCountdownComplete use a callback to ensure selectedPhotos is current
  const handleCountdownComplete = (): void => {
    if (!isMountedRef.current) return
    
    logActivity('Shared countdown expired on Select page, proceeding with selected photos.')
    setIsSelectionLocked(true)
    setSelectedPhotos((prevSelected: string[]) => {
      let finalSelection = prevSelected

      if (prevSelected.length < maxSelectablePhotos) {
        const remainingNeeded = maxSelectablePhotos - prevSelected.length
        const unselectedPhotos = gallery.capturedItems
          .filter((item) => !prevSelected.includes(item.photoUrl))
          .slice(0, remainingNeeded)
        const autoSelected = unselectedPhotos.map((item) => item.photoUrl)
        finalSelection = [...prevSelected, ...autoSelected]
      }

      // Use shorter timeout and check mount before navigation
      setTimeout(() => {
        if (isMountedRef.current) {
          handleContinueWithPhotos(finalSelection)
        }
      }, 500)

      return finalSelection
    })
  }

  const handleContinueWithPhotos = async (photosToProcess: string[]): Promise<void> => {
    // Check processing lock
    if (isProcessingRef.current) {
      console.warn('[Select] Already processing, ignoring duplicate request')
      return
    }
    
    if (!isMountedRef.current) return
    
    isProcessingRef.current = true
    setIsUploading(true)
    setIsSelectionLocked(true)
    const processingStartedAt = Date.now()
    let didAttemptPrint = false

    try {
      // Check if Edit page is enabled - use both methods for reliability
      const editSettings = useConfigStore.getState().getPageSettings('edit')
      const editEnabled =
        editSettings?.enabled ?? globalConfig?.pages.edit.settings.enabled ?? false

      // Ensure PrintPreview is ready before exporting (with timeout)
      try {
        if (previewRef.current) {
          await Promise.race([
            previewRef.current.waitForReady(8000),
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error('waitForReady timeout')), 8000)
            )
          ])
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error'
        console.warn('[Select] PrintPreview waitForReady timeout, continuing anyway:', errorMsg)
        logActivity(`PrintPreview waitForReady timeout: ${errorMsg}`, { level: 'warn' })
        // Continue anyway - export might still work
      }

      // Generate photo strip (with export timeout)
      const exportPromise = previewRef.current?.exportImage()
      if (!exportPromise) {
        throw new Error(t('select.stripExportFailed', 'Failed to create strip preview'))
      }

      const exportTimeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Export timeout')), 5000)
      )

      let stripBlob: Blob
      try {
        stripBlob = await Promise.race([exportPromise, exportTimeout])
        if (!stripBlob) {
          throw new Error(t('select.stripExportFailed', 'Failed to create strip preview'))
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error'
        console.error('[Select] Strip export failed:', errorMsg)
        logActivity(`Strip export failed: ${errorMsg}`, { level: 'error' })
        
        // Show user-friendly error but continue processing
        cogoToast.error(
          t('select.exportFailed', 'Failed to generate strip. Continuing without frame...')
        )
        
        // Re-throw to maintain existing error handling flow
        throw new Error(`Strip export failed: ${errorMsg}`)
      }

      // Note: Watermarks are only applied to individual photos, not strips
      // Strips should remain clean for editing and video processing

      // If Edit is enabled, keep video processing and related steps in this page
      // Upload the plain strip first, update gallery, and start video processing
      if (editEnabled) {
        console.log('[Select] Edit enabled - uploading plain strip, deferring printing and video processing')

        const ctx: ProcessingContext = {
          context,
          projectId,
          sessionId: id || '',
          galleryId: galleryKey || gallery.id,
          downloadLocal,
          isOnline: isOnlineRef.current
        }
        const stripResult = await processAndUploadStrip(stripBlob, ctx)
        const stripUrl = stripResult?.localUrl ?? URL.createObjectURL(stripBlob)
        
        if (stripResult?.localUrl) {
          setSessionState({
            gallery: {
              capturedItems: [
                ...gallery.capturedItems,
                {
                  id: `dreambooth-strip-${id}`,
                  stripUrl: stripResult.localUrl,
                  photoUrl: '',
                  timestamp: new Date()
                }
              ],
              stripUrl
            }
          })
        } else {
          setSessionState({
            gallery: {
              stripUrl: stripUrl
            }
          })
        }
        
        console.log('[Select] Navigating to Edit page (enabled)')
        if (isMountedRef.current) {
          navigate(`/${projectId}/edit`)
        }
        return
      }

      // Edit disabled - process everything as before
      console.log('[Select] Edit disabled - processing all outputs')

      const stripCtx: ProcessingContext = {
        context,
        projectId,
        sessionId: id || '',
        galleryId: galleryKey || gallery.id,
        downloadLocal,
        isOnline: isOnlineRef.current
      }

      // Print strip immediately after creation (blocking for initiation only, with feedback)
      if (
        isPrintingEnabled &&
        userPreferences.usePrint &&
        userPreferences.copies > 0 &&
        !printJobSentRef.current
      ) {
        didAttemptPrint = true
        printJobSentRef.current = true // Prevent duplicate print jobs
        const { printerName, settings } = getPrinterForFrame(userPreferences.frameId || '')
        logActivity('Sending print job', {
          copies: userPreferences.copies,
          printerName,
          pageSize: settings.pageSize,
          orientation: settings.orientation,
          margins: settings.margins,
          dpi: settings.dpi,
          scaleFactor: settings.scaleFactor,
          printBackground: settings.printBackground,
          printResolutionScale: settings.printResolutionScale,
          frameId: userPreferences.frameId
        })
        
        // Initiate print and wait for job to be sent (blocking for initiation only)
        // Show toast immediately when print job starts, not after completion
        cogoToast.success(t('select.printInitiated', 'Printing Initialized'))
        try {
          await initiatePrint(
            stripBlob,
            printerName,
            userPreferences.copies,
            settings,
            isOnlineRef.current
          )
          // Toast already shown above - no need to show again
        } catch (error) {
          // Only suppress if offline and error is network-related
          const errorMsg = error instanceof Error ? error.message : String(error)
          const isNetworkError =
            !isOnlineRef.current && (errorMsg.includes('network') || errorMsg.includes('fetch'))
          if (!isNetworkError || isOnlineRef.current) {
            cogoToast.error(`${t('select.printFailed', 'Failed to print')}: ${errorMsg}`)
          }
        }
      }

      const uploadPromise = processAndUploadStrip(stripBlob, stripCtx)
      const timeoutPromise = new Promise<{ localUrl: string }>((resolve) => {
        setTimeout(() => resolve({ localUrl: URL.createObjectURL(stripBlob) }), 2000)
      })
      const stripResult = await Promise.race([
        uploadPromise.then((r) => (r ? { localUrl: r.localUrl } : { localUrl: URL.createObjectURL(stripBlob) })),
        timeoutPromise
      ])

      if (stripResult.localUrl) {
        setSessionState({
          gallery: {
            capturedItems: [
              ...gallery.capturedItems,
              {
                id: `dreambooth-strip-${id}`,
                stripUrl: stripResult.localUrl,
                photoUrl: '',
                timestamp: new Date()
              }
            ]
          }
        })
      }

      // Generate and upload framed video in background with proper mirroring
      if (
        userPreferences.useRecording &&
        ['recording', 'strip_live', 'strip_story'].includes(userPreferences.recordingType || '')
      ) {
        // CRITICAL: Find items by matching position/index, not by photoUrl
        // This is because retaking updates the photoUrl to a new blob URL, but the item ID stays the same
        // We need to match photosToProcess (which may have old photoUrls) with current gallery items
        const photosOnly = gallery.capturedItems.filter(
          (item) =>
            item.photoUrl &&
            !item.id?.includes('dreambooth-strip') &&
            !item.id?.includes('dreambooth-gif')
        )
        const firstNPhotos = photosOnly.slice(0, maxSelectablePhotos)
        
        // Log initial state for debugging
        console.log(`[Select] Building resources array for ${photosToProcess.length} photos (maxSelectablePhotos: ${maxSelectablePhotos})`)
        console.log(`[Select] Gallery has ${photosOnly.length} photo items, firstNPhotos: ${firstNPhotos.length}`)
        console.log(`[Select] photosToProcess:`, photosToProcess)
        console.log(`[Select] firstNPhotos IDs:`, firstNPhotos.map((p, i) => ({ index: i, id: p.id, photoUrl: p.photoUrl, recordingUrl: p.recordingUrl ? 'present' : 'missing' })))
        
        const resources = photosToProcess
          .map((photoUrl, index) => {
            // Try to find by photoUrl first (for non-retaken photos)
            let item = gallery.capturedItems.find((item) => item.photoUrl === photoUrl)
            let matchMethod = 'photoUrl'
            
            // If not found by photoUrl, find by position/index (for retaken photos)
            // This handles the case where photoUrl changed during retake but position stayed the same
            if (!item && index < firstNPhotos.length) {
              item = firstNPhotos[index]
              matchMethod = 'index'
              console.log(`[Select] Photo ${photoUrl} not found by URL, using item at index ${index} (ID: ${item.id}, new photoUrl: ${item.photoUrl}, recordingUrl: ${item.recordingUrl ? 'present' : 'missing'})`)
            }
            
            // Additional fallback: Try to find by item ID if photosToProcess contains IDs
            // This handles edge cases where both photoUrl and index matching fail
            if (!item) {
              // Try to find by matching with firstNPhotos using a more flexible approach
              // Check if any firstNPhotos item has a photoUrl that matches any pattern
              const potentialMatch = firstNPhotos.find((_, idx) => idx === index)
              if (potentialMatch) {
                item = potentialMatch
                matchMethod = 'index-fallback'
                console.log(`[Select] Using index-fallback for photo at index ${index} (ID: ${item.id})`)
              }
            }
            
            if (!item) {
              console.error(`[Select] Could not find captured item for photo ${photoUrl} at index ${index}`)
              console.error(`[Select] Available photoUrls:`, gallery.capturedItems.map(i => i.photoUrl))
              console.error(`[Select] First N photos:`, firstNPhotos.map(i => ({ id: i.id, photoUrl: i.photoUrl, recordingUrl: i.recordingUrl ? 'present' : 'missing' })))
              console.error(`[Select] photosToProcess length: ${photosToProcess.length}, maxSelectablePhotos: ${maxSelectablePhotos}`)
              throw new Error(`Could not find captured item for photo ${photoUrl} at index ${index}`)
            }

            // Enhanced debug logging for video processing
            const hasRecordingUrl = item.recordingUrl && 
              item.recordingUrl.trim() !== '' && 
              (item.recordingUrl.startsWith('blob:') || item.recordingUrl.startsWith('http'))
            
            if (hasRecordingUrl && item.recordingUrl) {
              console.log(`[Select] ✓ Found recordingUrl for photo at index ${index} (matched by ${matchMethod}, photoUrl: ${photoUrl}, item photoUrl: ${item.photoUrl}, recordingUrl: ${item.recordingUrl.substring(0, 50)}...)`)
            } else {
              console.warn(`[Select] ✗ No valid recordingUrl found for photo at index ${index} (matched by ${matchMethod}, photoUrl: ${photoUrl}, item photoUrl: ${item.photoUrl})`)
              console.warn(`[Select] Item recordingUrl value: "${item.recordingUrl}" (type: ${typeof item.recordingUrl})`)
              console.warn(`[Select] Item ID: "${item.id}", timestamp: ${item.timestamp}`)
              
              // Check if this is expected to have a recordingUrl (strip_live package)
              if (userPreferences.recordingType === 'strip_live') {
                console.error(`[Select] ERROR: Photo at index ${index} is missing recordingUrl but strip_live is enabled! This should not happen.`)
                logActivity(`Photo at index ${index} missing recordingUrl for strip_live`, {
                  level: 'error',
                  index,
                  photoUrl,
                  itemPhotoUrl: item.photoUrl,
                  itemId: item.id,
                  recordingType: userPreferences.recordingType
                })
              }
            }

            return {
              photoUrl: item.photoUrl,
              videoUrl: item.recordingUrl || '',
              _matchMethod: matchMethod,
              _itemId: item.id
            }
          })
          .map((resource) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { _matchMethod, _itemId, ...cleanResource } = resource
            return cleanResource
          })

        // Pass all resources through; generateFramedVideo/generateLiveMode handle empty videoUrl with static photo fallback
        const resourcesWithMissingVideo = resources.filter(
          (r) =>
            !r.videoUrl ||
            r.videoUrl.trim() === '' ||
            (!r.videoUrl.startsWith('blob:') && !r.videoUrl.startsWith('http'))
        )
        if (resourcesWithMissingVideo.length > 0) {
          console.log(`[Select] ${resourcesWithMissingVideo.length} resource(s) have missing/empty videoUrl; video processor will use static photo fallback`)
          logActivity(`Resources with missing videoUrl (static photo fallback): ${resourcesWithMissingVideo.length}`, {
            level: 'warn',
            totalResources: resources.length,
            recordingType: userPreferences.recordingType
          })
        }

        if (resources.length > 0) {
          // Get photo states from PrintPreview to ensure mirroring consistency
          const photoStates = previewRef.current?.getPhotoStates() || {}

          // Generate both story and live mode videos
          const taskIds = {
            sequential: `video-story-${id}-${Date.now()}`,
            live: `video-live-${id}-${Date.now()}`
          }
          setVideoProcessingId(
            userPreferences.recordingType === 'strip_story' ? taskIds.sequential : taskIds.live
          ) // Track sequential video initially

          // Start sequential video processing
          // CRITICAL: Always use unscaledFrameConfig (scale = 1) for video processing
          // Video generation will automatically resize if dimensions exceed codec limits (1920px),
          // but using unscaled frames prevents unnecessary processing and ensures consistency
          videoProcessingIdRef.current = taskIds.sequential
          // CRITICAL: captureCountdown is in seconds, but generateFramedVideo expects milliseconds
          // Convert seconds to milliseconds (same as Capture.tsx does)
          const captureCountdownSeconds = globalConfig?.pages.capture.settings.captureCountdown || 3
          const captureCountdownMs = captureCountdownSeconds * 1000
          backgroundVideoProcessor.processVideo({
            id: taskIds.sequential,
            type: userPreferences.recordingType === 'strip_story' ? 'story' : 'live',
            resources,
            frameConfig: unscaledFrameConfig, // Always unscaled - independent of print resolution
            sessionId: id || '',
            userId: context?.user?.email || '',
            projectId: projectId || '',
            galleryId: galleryKey || gallery.id,
            speed: globalConfig?.pages.capture.settings.recordingSpeed || 1,
            fps: 30,
            photoStates: photoStates, // Use adjusted photo states
            countdown: captureCountdownMs,
            onError: (error) => {
              if (!isMountedRef.current) return
              
              const errorMessage = error instanceof Error ? error.message : 'Unknown error'
              console.error('[Select] Error processing video:', errorMessage, {
                taskId: taskIds.sequential,
                recordingType: userPreferences.recordingType,
                photosCount: photosToProcess.length
              })
              logActivity(`Video processing failed: ${errorMessage}`, {
                taskId: taskIds.sequential,
                recordingType: userPreferences.recordingType,
                photosCount: photosToProcess.length,
                error: errorMessage,
                level: 'error'
              })
              if (isMountedRef.current) {
                cogoToast.error(t('select.errorVideo', 'Error processing video'))
                setVideoProcessingId(null)
              }
              videoProcessingIdRef.current = null
            }
          })
        } else {
          console.warn('[Select] No resources to process, skipping video')
        }
      }

      console.log('[Select] Navigating to Result page (Edit disabled)')
      // Note: Final session upload with isCompleted will be handled by Result page
      const minDisplaySec = useSettingsStore.getState().printResultMinDisplaySeconds
      if (didAttemptPrint && minDisplaySec > 0) {
        await waitPrintResultMinDisplay(processingStartedAt, minDisplaySec, {
          isStillMounted: () => isMountedRef.current
        })
      }
      if (isMountedRef.current) {
        navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
      }
    } catch (error) {
      console.error('Error in continue process:', error)
      cogoToast.error(
        t(
          'select.errorProcessing',
          'There was an error processing your photos. Please report this issue.'
        )
      )

      // Log processing failure
      logActivity(
        `Processing failed for ${photosToProcess.length} photos. Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
      // Check if Edit page is enabled - use both methods for reliability
      const editSettings = useConfigStore.getState().getPageSettings('edit')
      const editEnabled =
        editSettings?.enabled ?? globalConfig?.pages.edit.settings.enabled ?? false

      if (editEnabled) {
        console.log('[Select] Error handler: Navigating to Edit page (enabled)')
        if (isMountedRef.current) {
          navigate(`/${projectId}/edit`)
        }
      } else {
        console.log('[Select] Error handler: Navigating to Result page (Edit disabled)')
        if (isMountedRef.current) {
          navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
        }
      }
    } finally {
      if (isMountedRef.current) {
        setIsUploading(false)
        setIsSelectionLocked(false)
      }
      isProcessingRef.current = false
    }
  }

  // Initialize countdown and handle initial media uploads
  useEffect((): void | (() => void) => {
    isMountedRef.current = true
    
    stateUploader.uploadSessionState()

    // Initialize media uploads
    const initializeMedia = async (): Promise<void> => {
      // Prevent concurrent upload attempts
      if (uploadStartedRef.current) {
        return
      }
      uploadStartedRef.current = true

      if (userPreferences.useGIF) {
        (async (): Promise<void> => {
          try {
            const photoUrls = gallery.capturedItems
              .filter((item) => item.photoUrl)
              .map((item) => item.photoUrl!) as string[]

            const gifSpeed = globalConfig?.pages.capture.settings.gifSpeed || 1000
            const customGifFrame = globalConfig?.pages.capture.settings.customGifFrame

            const initCtx: ProcessingContext = {
              context,
              projectId,
              sessionId: id || '',
              galleryId: galleryKey || gallery.id,
              downloadLocal,
              isOnline: isOnlineRef.current
            }

            const { gifLocalUrl, mp4LocalUrl } = await processAndUploadGIF(
              photoUrls,
              gifSpeed,
              initCtx,
              customGifFrame
            )

            if (isMountedRef.current) {
              const newItems: Array<{
                id: string
                animationUrl: string
                animationType: 'gif' | 'mp4'
                gifUrl: string
                photoUrl: string
                timestamp: Date
              }> = []
              if (gifLocalUrl) {
                newItems.push({
                  id: `dreambooth-animation-gif-${id}`,
                  animationUrl: gifLocalUrl,
                  animationType: 'gif' as const,
                  gifUrl: gifLocalUrl,
                  photoUrl: '',
                  timestamp: new Date()
                })
              }
              if (mp4LocalUrl) {
                newItems.push({
                  id: `dreambooth-animation-mp4-${id}`,
                  animationUrl: mp4LocalUrl,
                  animationType: 'mp4' as const,
                  gifUrl: '',
                  photoUrl: '',
                  timestamp: new Date()
                })
              }

              if (newItems.length > 0) {
                setSessionState({
                  gallery: {
                    capturedItems: [...gallery.capturedItems, ...newItems]
                  }
                })
              }
            }
          } catch (error) {
            console.error('[Select] GIF/MP4 creation/upload failed:', error)
            if (isOnlineRef.current && isMountedRef.current) {
              cogoToast.error(t('select.errorGif', 'Error creating animations'))
            }
          }
        })()
      }

      const photoCtx: ProcessingContext = {
        context,
        projectId,
        sessionId: id || '',
        galleryId: galleryKey || gallery.id,
        downloadLocal,
        isOnline: isOnlineRef.current
      }
      const photoItems = gallery.capturedItems
        .filter((item) => item.photoUrl && item.photoUrl.trim() !== '')
        .map((item) => ({ photoUrl: item.photoUrl! }))
      processAndUploadPhotos(photoItems, photoCtx)
        .then(() => {
          const g = useSessionStateStore.getState().gallery
          setSessionState({
            gallery: {
              ...g,
              individualPhotosUploaded: true,
              individualPhotosUploadedAt: Date.now()
            }
          })
        })
        .catch((error) => {
          console.warn('[Select] Photo uploads failed:', error)
        })
    }

    initializeMedia()

    return (): void => {
      isMountedRef.current = false
    }
  }, [])

  // Handle global countdown expiration - auto-continue (both standard and frame-based mode)
  // When timeout expires, auto-select photos if needed, then auto-process
  useEffect(() => {
    if (
      isSharedCountdownExpired &&
      !isUploading &&
      !navigationLockRef.current &&
      isMountedRef.current
    ) {
      navigationLockRef.current = true
      logActivity('Shared countdown expired on Select page, auto-continuing')
      
      // Auto-select photos if none are selected
      if (selectedPhotos.length === 0) {
        const photosOnly = gallery.capturedItems.filter(
          (item) =>
            item.photoUrl &&
            !item.id?.includes('dreambooth-strip') &&
            !item.id?.includes('dreambooth-gif')
        )
        const autoSelected = photosOnly.slice(0, maxSelectablePhotos).map((item) => item.photoUrl || '')
        setSelectedPhotos(autoSelected)
        
        // Wait for selection to update, then process
        setTimeout(() => {
          if (isMountedRef.current) {
            handleCountdownComplete()
          }
        }, 100)
      } else {
        // Photos already selected, process immediately
        handleCountdownComplete()
      }
      
      // Reset lock after navigation completes
      setTimeout(() => {
        navigationLockRef.current = false
      }, 2000)
    }
  }, [isSharedCountdownExpired, selectedPhotos.length, isUploading, gallery.capturedItems, maxSelectablePhotos])

  // Check internet connectivity for offline detection
  useEffect(() => {
    if (typeof window.api !== 'undefined' && window.api.getInternetStatus) {
      window.api.getInternetStatus().then((status: boolean) => {
        isOnlineRef.current = status
      })

      const unsubscribe = window.api.onInternetStatusChanged((status: boolean) => {
        isOnlineRef.current = status
      })

      return unsubscribe
    }
    return (): void => {}
  }, [])

  // Add effect to check video processing status
  useEffect(() => {
    if (!videoProcessingId) return

    const checkStatus = (): void => {
      const isProcessing = backgroundVideoProcessor.isTaskProcessing(videoProcessingId)
      if (!isProcessing) {
        if (isMountedRef.current) {
          setVideoProcessingId(null)
        }
        videoProcessingIdRef.current = null
      }
    }

    const interval = setInterval(checkStatus, 1000)
    return (): void => clearInterval(interval)
  }, [videoProcessingId])
  
  // Cleanup video processing on unmount
  useEffect(() => {
    return (): void => {
      if (videoProcessingIdRef.current) {
        // Clear ref - video processing will continue in background but won't update state
        videoProcessingIdRef.current = null
      }
    }
  }, [])

  // Wait for PrintPreview to be ready
  useEffect(() => {
    const waitForPreview = async (): Promise<void> => {
      if (previewRef.current) {
        try {
          await previewRef.current.waitForReady()
          setIsPreviewReady(true)
        } catch (error) {
          console.error('Error waiting for preview to be ready:', error)
          // Fallback: enable after a timeout
          setTimeout(() => {
            setIsPreviewReady(true)
          }, 5000)
        }
      }
    }
    waitForPreview()
  }, [selectedPhotos, frameConfig])

  const handlePhotoClick = (photoUrl: string): void => {
    if (isSelectionLocked) return

    setSelectedPhotos((prev) => {
      let newSelection
      if (prev.includes(photoUrl)) {
        newSelection = prev.filter((url) => url !== photoUrl)
      } else if (prev.length < maxSelectablePhotos) {
        newSelection = [...prev, photoUrl]
      } else {
        newSelection = prev
      }
      return newSelection
    })
  }

  // Modified handleContinue to use the new function
  const handleContinue = async (): Promise<void> => {
    // Check if preview is ready, show warning if not
    if (!isPreviewReady) {
      cogoToast.warn('Preview is still loading. Please wait...')
      return
    }

    await handleContinueWithPhotos(selectedPhotos)
  }

  // Handle global countdown expiration - auto-continue (already handled in useEffect below)

  // Update the loading state to include video processing
  const isLoading: boolean = isUploading || videoProcessingId !== null

  // Update the button text to show video processing state
  const buttonText: string = isLoading
    ? videoProcessingId
      ? t('select.processingVideo', 'Processing Video...')
      : t('select.processing', 'Processing...')
    : t('select.next', 'Next ({{count}}/{{max}})', {
        count: selectedPhotos.length,
        max: maxSelectablePhotos
      })

  // Auto-select and auto-process photos when Select is disabled
  useEffect(() => {
    if (!config?.settings.enabled && selectedPhotos.length > 0 && !isUploading) {
      // Auto-process when photos are selected and page is disabled
      const autoProcess = async (): Promise<void> => {
        try {
          if (previewRef.current) {
            await previewRef.current.waitForReady()
          }
          // Small delay to ensure everything is ready
          await new Promise((resolve) => setTimeout(resolve, 500))
          await handleContinueWithPhotos(selectedPhotos)
        } catch (error) {
          console.error('[Select] Error in auto-process (disabled mode):', error)
          // Fallback: process anyway
          await handleContinueWithPhotos(selectedPhotos)
        }
      }
      autoProcess()
    }
  }, [config?.settings.enabled, selectedPhotos.length, isUploading])

  // Auto-select photos when Select is disabled
  useEffect(() => {
    if (!config?.settings.enabled && selectedPhotos.length === 0) {
      // Calculate how many photos we need based on frame configuration
      const photoSlots = frameConfig.drawParams.images.filter((img) =>
        img.type.startsWith('photo-')
      ).length

      // Auto select first N photos
      const autoSelected = gallery.capturedItems.slice(0, photoSlots)
      const selectedUrls = autoSelected.map((item) => item.photoUrl)
      setSelectedPhotos(selectedUrls)
    }
  }, [config?.settings.enabled, frameConfig, gallery.capturedItems, selectedPhotos.length])

  // Frame-based mode: Auto-select photos based on frame configuration
  useEffect(() => {
    if (captureMode === 'frame-based' && selectedFrame && selectedPhotos.length === 0) {
      // Auto-select photos based on frame configuration
      const photosOnly = gallery.capturedItems.filter(
        (item) =>
          item.photoUrl &&
          !item.id?.includes('dreambooth-strip') &&
          !item.id?.includes('dreambooth-gif')
      )
      const autoSelected = photosOnly.slice(0, maxSelectablePhotos).map((item) => item.photoUrl || '')
      
      if (autoSelected.length === maxSelectablePhotos) {
        setSelectedPhotos(autoSelected)
      }
    }
  }, [captureMode, selectedFrame, gallery.capturedItems, maxSelectablePhotos, selectedPhotos.length])

  // CRITICAL: Update selectedPhotos when gallery items change (e.g., after retake)
  // This ensures selectedPhotos always uses the latest photoUrls after retakes
  useEffect(() => {
    if (captureMode === 'frame-based' && selectedPhotos.length > 0) {
      const photosOnly = gallery.capturedItems.filter(
        (item) =>
          item.photoUrl &&
          !item.id?.includes('dreambooth-strip') &&
          !item.id?.includes('dreambooth-gif')
      )
      const firstNPhotos = photosOnly.slice(0, maxSelectablePhotos)
      
      // Check if any photoUrls in selectedPhotos don't match current gallery items
      // This happens when photos are retaken (photoUrl changes but position stays same)
      const needsUpdate = selectedPhotos.some((oldPhotoUrl, index) => {
        if (index >= firstNPhotos.length) return false
        const currentItem = firstNPhotos[index]
        return currentItem.photoUrl !== oldPhotoUrl
      })
      
      if (needsUpdate) {
        console.log('[Select] Updating selectedPhotos to match retaken photos')
        const updatedSelectedPhotos = firstNPhotos.slice(0, selectedPhotos.length).map((item) => item.photoUrl || '')
        setSelectedPhotos(updatedSelectedPhotos)
      }
    }
  }, [gallery.capturedItems, captureMode, maxSelectablePhotos, selectedPhotos.length])

  // If select page is disabled, show only processing modal
  if (!config?.settings.enabled) {
    return (
      <div className="relative w-full h-full bg-whiteout">
        {/* Shared Countdown Display - only show in frame-based mode */}
        {captureMode === 'frame-based' && (
          <div className="absolute top-8 right-8 z-50">
            <div className="border-2 border-black p-4 text-center rounded-lg">
              <div className="text-sm text-black mb-1">Time Remaining</div>
              <div className="font-bold text-black text-2xl">
                {sharedRemainingTime}
              </div>
            </div>
          </div>
        )}
        <ProcessingModal
          isVisible={true}
          title={t('select.processing', 'Processing...')}
          message={t('select.pleaseWait', 'Please wait while we process your photos...')}
          primaryColor={primaryColor}
        />
        {/* Hidden PrintPreview for strip export */}
        <div
          style={{
            position: 'absolute',
            visibility: 'hidden',
            width: `${frameConfig.drawParams.canvasWidth}px`,
            height: `${frameConfig.drawParams.canvasHeight}px`,
            top: '-9999px',
            left: '-9999px'
          }}
        >
          <PrintPreview
            ref={previewRef}
            frameConfig={frameConfig}
            selectedPhotos={selectedPhotos.length > 0 ? selectedPhotos.map((photo) =>
              mirroredPhotos.has(photo) ? `mirror:${photo}` : photo
            ) : gallery.capturedItems.filter((item) => item.photoUrl).slice(0, maxSelectablePhotos).map((item) => item.photoUrl || '')}
            isInteractive={false}
            sessionId={id || ''}
          />
        </div>
      </div>
    )
  }

  // Frame-based mode UI - centered frame preview matching Capture.tsx design
  if (captureMode === 'frame-based' && selectedFrame) {
    // Navigate to frame selection if no frame selected
    if (!selectedFrame) {
      navigate(`/${projectId}/frame`)
      return null
    }

    // Get photos for frame-based mode
    const frameBasedPhotos = selectedPhotos.length > 0 
      ? selectedPhotos 
      : gallery.capturedItems
          .filter((item) => item.photoUrl && !item.id?.includes('dreambooth-strip') && !item.id?.includes('dreambooth-gif'))
          .slice(0, maxSelectablePhotos)
          .map((item) => item.photoUrl || '')

    return (
      <div className="w-full h-full bg-newspaper-white font-newspaper text-[#1a1a1a] relative flex flex-col overflow-hidden p-4">
        {/* Shared Countdown Display */}
        <div className="absolute top-8 right-8 z-50">
          <div className="border-2 border-double border-[#2a2a2a] bg-newspaper-white p-6 text-center">
            <div className="text-2xl text-newspaper-black mb-2 font-newspaper">Time Remaining</div>
            <div className="font-bold text-newspaper-black text-5xl font-newspaper">{sharedRemainingTime}</div>
          </div>
        </div>
        {/* Standardized ProcessingModal for loading states */}
        <ProcessingModal
          isVisible={isUploading || videoProcessingId !== null}
          title={
            videoProcessingId
              ? t('select.processingVideo', 'Processing Video...')
              : t('select.processing', 'Processing...')
          }
          message={t('select.pleaseWait', 'Please wait while we process your photos...')}
          primaryColor={primaryColor}
        />

        {/* Header with Title and Mirror Button */}
        <div className="absolute top-8 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-6">
          <h1 className="text-5xl font-newspaper font-bold text-newspaper-black uppercase tracking-wider text-center">
            {t('select.title', 'Arrange your photos')}
          </h1>
          <p className="text-2xl font-newspaper text-newspaper-black/70 text-center max-w-2xl">
            {t('select.instruction', 'Tap photos to select, swipe to move, pinch to zoom')}
          </p>
          <button
            type="button"
            onClick={() => {
              if (isSelectionLocked) return
              // Mirror all photos
              const newMirroredPhotos = new Set(mirroredPhotos)
              const willMirrorAll = frameBasedPhotos.some(
                (photo) => !mirroredPhotos.has(photo)
              )

              frameBasedPhotos.forEach((photo) => {
                if (willMirrorAll) {
                  newMirroredPhotos.add(photo)
                } else {
                  newMirroredPhotos.delete(photo)
                }
              })

              setIsAllMirrored(willMirrorAll)
              setMirroredPhotos(newMirroredPhotos)
            }}
            disabled={isSelectionLocked}
            title={
              isSelectionLocked
                ? t('select.selectionLocked', 'Selection locked')
                : t('select.mirror', 'Mirror Photos')
            }
            className={`flex items-center gap-4 px-10 py-5 rounded-lg transition-colors shadow-lg border-2 border-double ${
              isAllMirrored
                ? 'bg-[#1a1a1a] text-[#fafaf8] border-[#1a1a1a] hover:bg-[#2a2a2a]'
                : 'bg-white hover:bg-gray-100 text-[#1a1a1a] border-[#2a2a2a]'
            } ${isSelectionLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <FaExchangeAlt className={`text-3xl ${isAllMirrored ? 'text-[#fafaf8]' : 'text-[#1a1a1a]'}`} />
            <span className={`${isAllMirrored ? 'text-[#fafaf8]' : 'text-[#1a1a1a]'} font-bold text-2xl`}>
              {t('select.mirror', 'Mirror')}
            </span>
          </button>
        </div>

        {/* Frame Preview Area - Takes available space (matching Capture.tsx) */}
        <div className="flex-1 min-h-0 relative flex items-center justify-center pt-40">
          {/* Scaled Frame Display */}
          <div
            className="absolute flex items-center justify-center"
            style={{
              width: `${frameWidth * scale}px`,
              height: `${frameHeight * scale}px`
            }}
          >
            <PrintPreview
              ref={previewRef}
              frameConfig={frameConfig}
              selectedPhotos={frameBasedPhotos.map((photo) =>
                mirroredPhotos.has(photo) ? `mirror:${photo}` : photo
              )}
              isInteractive={true}
              className="w-full h-full"
              sessionId={id || ''}
            />
          </div>
        </div>

        {/* Button Area - Fixed height, no overlap (matching Capture.tsx) */}
        <div className="flex-shrink-0 relative h-44 flex items-center justify-center">
          <button
            type="button"
            onClick={handleContinue}
            disabled={
              frameBasedPhotos.length !== maxSelectablePhotos || isUploading || !isPreviewReady
            }
            className="hover:opacity-90 cursor-pointer font-newspaper font-bold transition-all duration-500 select-none active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-[#1a1a1a] text-[#fafaf8] border-2 border-double border-[#2a2a2a] text-4xl px-16 py-5 min-w-[320px] tracking-wider rounded-none shadow-none"
          >
            {isLoading
              ? videoProcessingId
                ? t('select.processingVideo', 'Processing Video...')
                : t('select.processing', 'Processing...')
              : t('select.continue', 'Continue')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full bg-newspaper-white font-newspaper text-[#1a1a1a] relative flex flex-col overflow-hidden p-4">
      {/* Shared Countdown Display */}
      <div className="absolute top-8 right-8 z-50">
        <div className="border-2 border-double border-[#2a2a2a] bg-newspaper-white p-6 text-center">
          <div className="text-2xl text-newspaper-black mb-2 font-newspaper">Time Remaining</div>
          <div className="font-bold text-newspaper-black text-5xl font-newspaper">{sharedRemainingTime}</div>
        </div>
      </div>
      {/* Standardized ProcessingModal for loading states */}
      <ProcessingModal
        isVisible={isUploading || videoProcessingId !== null}
        title={
          videoProcessingId
            ? t('select.processingVideo', 'Processing Video...')
            : t('select.processing', 'Processing...')
        }
        message={t('select.pleaseWait', 'Please wait while we process your photos...')}
        primaryColor={primaryColor}
      />

      {/* Header with Title and Mirror Button */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-6">
        <h1 className="text-5xl font-newspaper font-bold text-newspaper-black uppercase tracking-wider text-center">
          {t('select.title', 'Arrange your photos')}
        </h1>
        <p className="text-2xl font-newspaper text-newspaper-black/70 text-center max-w-2xl">
          {t('select.instruction', 'Tap photos to select, swipe to move, pinch to zoom')}
        </p>
        <button
          type="button"
          onClick={() => {
            if (isSelectionLocked) return
            // Mirror all photos
            const newMirroredPhotos = new Set(mirroredPhotos)
            const willMirrorAll = gallery.capturedItems.some(
              (item) => !mirroredPhotos.has(item.photoUrl)
            )

            gallery.capturedItems.forEach((item) => {
              if (willMirrorAll) {
                newMirroredPhotos.add(item.photoUrl)
              } else {
                newMirroredPhotos.delete(item.photoUrl)
              }
            })

            setIsAllMirrored(willMirrorAll)
            setMirroredPhotos(newMirroredPhotos)
          }}
          disabled={isSelectionLocked}
          title={
            isSelectionLocked
              ? t('select.selectionLocked', 'Selection locked')
              : t('select.mirror', 'Mirror Photos')
          }
          className={`flex items-center gap-4 px-10 py-5 rounded-lg transition-colors shadow-lg border-2 border-double ${
            isAllMirrored
              ? 'bg-[#1a1a1a] text-[#fafaf8] border-[#1a1a1a] hover:bg-[#2a2a2a]'
              : 'bg-white hover:bg-gray-100 text-[#1a1a1a] border-[#2a2a2a]'
          } ${isSelectionLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <FaExchangeAlt className={`text-3xl ${isAllMirrored ? 'text-[#fafaf8]' : 'text-[#1a1a1a]'}`} />
          <span className={`${isAllMirrored ? 'text-[#fafaf8]' : 'text-[#1a1a1a]'} font-bold text-2xl`}>
            {t('select.mirror', 'Mirror')}
          </span>
        </button>
      </div>

      {/* Frame Preview Area - Takes available space (matching Capture.tsx) */}
      <div className="flex-1 min-h-0 relative flex items-center justify-center pt-40">
        {/* Scaled Frame Display */}
        <div
          className="absolute flex items-center justify-center"
          style={{
            width: `${frameWidth * scale}px`,
            height: `${frameHeight * scale}px`
          }}
        >
          <PrintPreview
            ref={previewRef}
            frameConfig={frameConfig}
            selectedPhotos={selectedPhotos.map((photo) =>
              mirroredPhotos.has(photo) ? `mirror:${photo}` : photo
            )}
            isInteractive={true}
            className="w-full h-full"
            sessionId={id || ''}
          />
        </div>

        {/* Photo Selection Overlay - Bottom of frame area */}
        {selectedPhotos.length < maxSelectablePhotos && !isSelectionLocked && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 bg-newspaper-white/95 border-2 border-double border-[#2a2a2a] p-4 max-w-[90%] rounded-none">
            <p className="text-center text-newspaper-black text-2xl font-newspaper mb-2">
              {t('select.selectPhotos', 'Select {{count}}/{{max}} photos', {
                count: selectedPhotos.length,
                max: maxSelectablePhotos
              })}
            </p>
            <div className={`grid ${isPotrait ? 'grid-cols-2' : 'grid-cols-4'} gap-3 max-h-32 overflow-y-auto`}>
              {gallery.capturedItems
                .filter((item) => item.photoUrl)
                .map((item, index) => {
                  const isSelected = selectedPhotos.includes(item.photoUrl || '')
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handlePhotoClick(item.photoUrl || '')}
                      className={`relative overflow-hidden rounded transition-all ${
                        isSelected ? 'ring-2 ring-[#1a1a1a]' : 'opacity-60 hover:opacity-100'
                      }`}
                      style={{
                        boxShadow: isSelected ? `0 0 0 2px ${primaryColor}` : ''
                      }}
                    >
                      <PhotoFrame
                        src={item.photoUrl || ''}
                        alt={`Photo ${index + 1}`}
                        displayRatio={1}
                        isMirrored={mirroredPhotos.has(item.photoUrl || '')}
                        className="w-20 h-20"
                      />
                      {isSelected && (
                        <div
                          className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {selectedPhotos.indexOf(item.photoUrl || '') + 1}
                        </div>
                      )}
                    </button>
                  )
                })}
            </div>
          </div>
        )}

        {/* Selection Locked Overlay */}
        {isSelectionLocked && (
          <div className="absolute inset-0 bg-carbon/5 backdrop-blur-[1px] z-20 flex items-center justify-center">
            <div className="bg-white/90 px-6 py-3 rounded-lg shadow-lg">
              <span className="text-2xl text-carbon">
                {isUploading
                  ? t('select.processing', 'Processing photos...')
                  : t('select.selectionLocked', 'Selection locked')}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Button Area - Fixed height, no overlap (matching Capture.tsx) */}
      <div className="flex-shrink-0 relative h-44 flex items-center justify-center">
        <button
          type="button"
          onClick={handleContinue}
          disabled={
            selectedPhotos.length !== maxSelectablePhotos || isUploading || !isPreviewReady
          }
          className="hover:opacity-90 cursor-pointer font-newspaper font-bold transition-all duration-500 select-none active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-[#1a1a1a] text-[#fafaf8] border-2 border-double border-[#2a2a2a] text-4xl px-16 py-5 min-w-[320px] tracking-wider rounded-none shadow-none"
        >
          {buttonText}
        </button>
      </div>
    </div>
  )
}

export default SelectPage
